import * as webllm from '@mlc-ai/web-llm';

import {
  normalizeKeywords,
  emptyParsedJobQuery,
  normalizeStructuredFilters,
} from '../utils';

import type { ParsedJobQuery } from '../utils';

const DEFAULT_MODEL = 'Qwen2.5-1.5B-Instruct-q4f16_1-MLC';

function buildQueryParsePrompt(query: string): string {
  const escapedQuery = query.replace(/"/g, '\\"');

  return `
You are a specialized JSON generator for a recruitment platform.

### TASK
Convert the user's job search query into a structured JSON object.

### SCHEMA
{
  "filters": {
    "jobMode": ("Remote" | "On-site" | "Hybrid")[] | null,
    "jobType": ("Full-Time" | "Part-Time" | "Contract" | "Internship" | "Temporary" | "Seasonal" | "Volunteer")[] | null,
    "location": string | null
  },
  "keywords": string[]
}

### EXTRACTION RULES
1. **Keywords**: Every specific role (e.g., "Developer", "Designer"), skill (e.g., "React", "Python"), or company name must go into the "keywords" array.
2. **Job Mode Mapping**:
   - "WFH", "Work from home", "Remote" → "Remote"
   - "Office", "WFO", "On-site" → "On-site"
   - "Flexible", "Hybrid" → "Hybrid"
3. **Job Type Mapping**:
   - "Intern" or "Internship" → "Internship"
   - "Contractor" or "Freelance" → "Contract"
   - "Full time" → "Full-Time"
4. **Strict JSON**: Output ONLY the JSON object. No markdown formatting, no explanations, and no text before or after the braces.
5. **No Data Case**: If a query only contains keywords (e.g., "Software Engineer"), set all "filters" keys to null.

### EXAMPLES
- User: "React developer in India"
- Output: {"filters": {"jobMode": null, "jobType": null, "location": "India"}, "keywords": ["React", "developer"]}

- User: "Full-time remote UI Designer"
- Output: {"filters": {"jobMode": ["Remote"], "jobType": ["Full-Time"], "location": null}, "keywords": ["UI", "Designer"]}

Query: "${escapedQuery}"
JSON Output:`.trim();
}

let engine: webllm.MLCEngine | null = null;
let engineInitPromise: Promise<webllm.MLCEngine> | null = null;

export type WebLLMHealth = {
  isWebGPUSupported: boolean;
  isInitialized: boolean;
  gpuVendor?: string;
  reason?: string;
};

function getBrowserNavigator(): (Navigator & { gpu?: unknown }) | undefined {
  return typeof navigator !== 'undefined'
    ? (navigator as Navigator & { gpu?: unknown })
    : undefined;
}

export function isWebGPUSupported(): boolean {
  return Boolean(getBrowserNavigator()?.gpu);
}

export function isEngineReady(): boolean {
  return engine !== null;
}

export function isEngineInitializing(): boolean {
  return engine === null && engineInitPromise !== null;
}

export async function checkWebLLMHealth(): Promise<WebLLMHealth> {
  if (!isWebGPUSupported()) {
    return {
      isWebGPUSupported: false,
      isInitialized: false,
      reason: 'WebGPU is not available in this browser/device.',
    };
  }

  try {
    const llmEngine = await initWebLLM();
    const gpuVendor = await llmEngine.getGPUVendor();
    return { isWebGPUSupported: true, isInitialized: true, gpuVendor };
  } catch (error) {
    return {
      isWebGPUSupported: true,
      isInitialized: false,
      reason: error instanceof Error ? error.message : 'Unknown WebLLM initialization error',
    };
  }
}

export async function initWebLLM(): Promise<webllm.MLCEngine> {
  if (engine) {
    return engine;
  }

  if (engineInitPromise) {
    return engineInitPromise;
  }

  if (!isWebGPUSupported()) {
    throw new Error('WebGPU is unavailable. WebLLM cannot be initialized.');
  }

  engineInitPromise = (async () => {
    try {
      const llmEngine = new webllm.MLCEngine();
      await llmEngine.reload(DEFAULT_MODEL);
      engine = llmEngine;
      return llmEngine;
    } catch (error) {
      console.error('[webllm] init failed:', error);
      engineInitPromise = null;
      throw error;
    }
  })();

  return engineInitPromise;
}

function extractContent(response: unknown): string {
  const typed = response as { choices?: Array<{ message?: { content?: string } }> };
  return typed?.choices?.[0]?.message?.content ?? '';
}

function parseJsonObject(rawText: string): unknown {
  const trimmed = rawText.trim();
  if (!trimmed) return null;

  try {
    const start = rawText.indexOf('{');
    const end = rawText.lastIndexOf('}');
    const jsonSlice = start >= 0 && end >= start ? rawText.slice(start, end + 1) : trimmed;
    const parsed = JSON.parse(jsonSlice);
    return parsed;
  } catch {
    return null;
  }
}

function buildFallbackParsedJobQuery(query: string): ParsedJobQuery {
  const fallback = emptyParsedJobQuery();
  const tokens = query
    .split(/\s+/)
    .map((token) => token.trim().toLowerCase())
    .filter((token) => token.length > 1);
  fallback.keywords = Array.from(new Set(tokens));
  return fallback;
}

export async function parseJobQueryWithWebLLM(
  query: string,
  shouldCancel?: () => boolean
): Promise<ParsedJobQuery> {
  if (!query.trim()) return emptyParsedJobQuery();
  if (shouldCancel?.()) return emptyParsedJobQuery();

  try {
    const llmEngine = await initWebLLM();
    if (shouldCancel?.()) return emptyParsedJobQuery();
    const response = await llmEngine.chat.completions.create({
      messages: [{ role: 'user', content: buildQueryParsePrompt(query) }],
    });

    if (shouldCancel?.()) return emptyParsedJobQuery();

    const parsed = parseJsonObject(extractContent(response)) as
      | { filters?: unknown; keywords?: unknown }
      | null;

    return {
      filters: normalizeStructuredFilters(parsed?.filters),
      keywords: normalizeKeywords(parsed?.keywords),
    };
  } catch (error) {
    if (shouldCancel?.()) return emptyParsedJobQuery();
    console.error('[webllm] query parsing failed:', error);
    return buildFallbackParsedJobQuery(query);
  }
}
